import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { TwoPaneLayout } from '../components/TwoPaneLayout';
import { StoreBadges } from '../components/StoreBadges';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import {
  ApiError,
  consumeOAuthFragment,
  inviteAccept,
  inviteClaim,
  inviteInfo,
  login,
  resendVerification,
  verifyEmail,
  type InviteInfoResponse,
} from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import PasswordField from '../components/PasswordField';

/**
 * Invite landing page — `/invite/:token`. The destination of the link
 * sent by the platform invite-create endpoint. See:
 *   - Docs/architecture/ARCH_WEB_USER_APP.md §3
 *   - Docs/plans/PLAN_PLATFORM_INVITATIONS.md §11
 *
 * Flow (P1-INV11b, 2026-05-21 — endpoints now platform-tier):
 *   1. On mount, GET /v1/auth/invite/info?token=... (public).
 *   2. If accepted/revoked/expired → friendly message + sign-in link.
 *   3. New account branch (emailExists=false):
 *        a. POST /auth/register {email, password, displayName, appId}
 *        b. POST /v1/auth/invite/accept {token}     (under fresh JWT)
 *      → bookkeeping done, JWT in hand, navigate /dashboard.
 *   4. Existing account branch (emailExists=true):
 *        a. POST /auth/login {email, password, appId}
 *        b. POST /v1/auth/invite/accept {token}     (under fresh JWT)
 *      → bookkeeping done, JWT in hand, navigate /dashboard.
 *
 * Splitting register/login from accept keeps user-creation on the
 * platform-native auth path (no re-implementing it inside a sandboxed
 * handler) while keeping invite bookkeeping platform-wide (so future
 * apps inherit the same flow without writing their own handler).
 */

type ViewState =
  | { kind: 'loading' }
  | { kind: 'createAccount'; info: InviteInfoResponse }
  | { kind: 'signIn'; info: InviteInfoResponse }
  | { kind: 'alreadyDone'; info: InviteInfoResponse; reason: 'accepted' | 'revoked' | 'expired' }
  | { kind: 'backendNotReady'; httpStatus: number; message: string }
  | { kind: 'error'; message: string };

export function InviteLanding() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [view, setView] = useState<ViewState>({ kind: 'loading' });
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // OTP verify step — only relevant after the createAccount branch
  // succeeds at /auth/register. We hold the password from step 1 so we
  // can call login() automatically once verify-email confirms.
  const [verifyMode, setVerifyMode] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);

  // Check for an OAuth callback fragment ON FIRST RENDER (before
  // looking up the invite). If the user just came back from Google,
  // we already have a JWT — finish the invite-accept and redirect.
  useEffect(() => {
    const oauth = consumeOAuthFragment();
    if (!oauth) return;
    if (!oauth.ok) {
      setSubmitError(oauth.error ?? 'Google sign-in failed.');
      return;
    }
    // Token already stored by consumeOAuthFragment(). Run accept.
    void (async () => {
      try {
        await inviteAccept(token);
      } catch (acceptErr) {
        // Surface but don't roll back — they're authenticated.
        setSubmitError(
          acceptErr instanceof Error
            ? acceptErr.message
            : 'Your account is signed in, but we couldn’t finalize the invitation.',
        );
      }
      await refresh();
      navigate('/dashboard', { replace: true });
    })();
  }, [navigate, refresh, token]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const info = await inviteInfo(token);
        if (cancelled) return;
        if (info.revoked) {
          setView({ kind: 'alreadyDone', info, reason: 'revoked' });
        } else if (info.expired) {
          setView({ kind: 'alreadyDone', info, reason: 'expired' });
        } else if (info.accepted) {
          setView({ kind: 'alreadyDone', info, reason: 'accepted' });
        } else if (info.emailExists) {
          setView({ kind: 'signIn', info });
        } else {
          setView({ kind: 'createAccount', info });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          // Handler not deployed yet — show a graceful "almost ready"
          // page so the recipient isn't dropped on a stack trace.
          setView({
            kind: 'backendNotReady',
            httpStatus: err.status,
            message: err.message,
          });
        } else {
          setView({
            kind: 'error',
            message:
              err instanceof Error ? err.message : 'Unable to look up invitation.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (view.kind !== 'createAccount' && view.kind !== 'signIn') return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (view.kind === 'createAccount') {
        // Single-shot invite claim — the invite token IS proof of email
        // ownership, so the server can set the password + flip
        // email_verified=true + activate the account + claim invited_role
        // + mint a session in one transaction. No OTP step, no separate
        // login/accept calls. See gateway POST /v1/auth/invite/claim
        // for the rationale (P1-INV04, 2026-05-22).
        //
        // Server returns 409 ALREADY_ACTIVE if the user already has a
        // password (someone registered for the email then later got
        // invited as staff) — bounce them to the SignIn branch.
        const [givenName, ...familyParts] = displayName.trim().split(/\s+/);
        const familyName = familyParts.join(' ').trim();
        try {
          await inviteClaim({
            token,
            password,
            givenName: givenName || undefined,
            familyName: familyName || undefined,
          });
        } catch (claimErr) {
          if (claimErr instanceof ApiError && claimErr.status === 409) {
            setSubmitError(
              'An account with that email already exists. Please sign in with your password instead.',
            );
            setView({ kind: 'signIn', info: view.info });
            return;
          }
          throw claimErr;
        }
        // Session adopted by inviteClaim() helper. Refresh app auth +
        // navigate to dashboard. Invited_role (if any) is already
        // claimed onto user_app_associations — no follow-up call.
        await refresh();
        navigate('/dashboard', { replace: true });
        return;
      }

      // SignIn branch — user already has a real password (registered
      // before this invite, or accepted a prior invite from a different
      // app). Normal /auth/login + /v1/auth/invite/accept tail.
      await login(view.info.inviteeEmail, password);
      await acceptInviteAndNavigate();
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : view.kind === 'createAccount'
            ? 'Unable to create your account.'
            : 'Unable to sign in.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Shared tail: POST /v1/auth/invite/accept (under our fresh JWT)
   * then refresh + navigate. If the bookkeeping post fails we keep
   * the user signed in and surface a partial-success message — they
   * can use the app and an admin can re-link the invite.
   */
  async function acceptInviteAndNavigate() {
    try {
      // Platform-tier endpoint. Requires the JWT we just minted.
      await inviteAccept(token);
    } catch (acceptErr) {
      const msg =
        acceptErr instanceof Error
          ? acceptErr.message
          : 'Your account is ready, but we couldn’t finalize the invitation. Please sign in normally and contact support.';
      setSubmitError(msg);
    }
    await refresh();
    navigate('/dashboard', { replace: true });
  }

  async function onSubmitVerify(ev: FormEvent) {
    ev.preventDefault();
    if (view.kind !== 'createAccount') return;
    setSubmitError(null);
    const digits = otp.replace(/\D/g, '');
    if (digits.length !== 6) {
      setSubmitError('The code is 6 digits.');
      return;
    }
    setVerifying(true);
    try {
      await verifyEmail(view.info.inviteeEmail, digits);
      // Email now verified — log in with the password from step 1 and
      // complete the invite-accept tail.
      await login(view.info.inviteeEmail, password);
      await acceptInviteAndNavigate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setSubmitError('That code is invalid or expired. Try again or resend.');
      } else {
        setSubmitError(
          err instanceof Error
            ? err.message
            : 'Verification failed. Please try again.',
        );
      }
    } finally {
      setVerifying(false);
    }
  }

  async function onResend() {
    if (view.kind !== 'createAccount') return;
    setResendBusy(true);
    setSubmitError(null);
    try {
      await resendVerification(view.info.inviteeEmail);
      setVerifyNotice("If your email is registered, a new code is on its way.");
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : 'Resend failed. Please check your connection.',
      );
    } finally {
      setResendBusy(false);
    }
  }

  function onBackFromVerify() {
    setVerifyMode(false);
    setOtp('');
    setVerifyNotice(null);
    setSubmitError(null);
  }

  return (
    <TwoPaneLayout>
      {view.kind === 'loading' && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="mt-3 text-muted">Looking up your invitation…</p>
        </div>
      )}

      {view.kind === 'error' && (
        <div>
          <h2 className="h4 mb-3">We couldn't open this invitation</h2>
          <p className="text-muted">{view.message}</p>
          <p className="text-muted">
            If you already have an AquaStar account,{' '}
            <Link to="/login">sign in here</Link>.
          </p>
        </div>
      )}

      {view.kind === 'backendNotReady' && (
        <div>
          <h2 className="h4 mb-3">Welcome to AquaStar</h2>
          <p className="text-muted">
            Your invitation link is valid — we're putting the finishing touches
            on the sign-up page. In the meantime, you can install the AquaStar
            mobile app and we'll be ready to take you the rest of the way very
            soon.
          </p>
          <hr className="my-4" />
          <p className="small text-muted mb-2">Or get the AquaStar app:</p>
          <StoreBadges />
        </div>
      )}

      {view.kind === 'alreadyDone' && (
        <div>
          <h2 className="h4 mb-3">
            {view.reason === 'accepted'
              ? 'This invitation has already been accepted'
              : view.reason === 'revoked'
                ? 'This invitation has been revoked'
                : 'This invitation has expired'}
          </h2>
          <p className="text-muted">
            {view.reason === 'accepted' ? (
              <>You can sign in to your AquaStar account whenever you're ready.</>
            ) : (
              <>
                Please reach out to the person who sent you this invitation and
                ask them to send a new one.
              </>
            )}
          </p>
          <Link className="btn btn-primary" to="/login">
            Sign in
          </Link>
        </div>
      )}

      {view.kind === 'createAccount' && verifyMode && (
        <form onSubmit={onSubmitVerify} noValidate>
          <h2 className="h4 mb-3">Verify your email</h2>
          <p className="text-muted small mb-2">
            We sent a 6-digit code to:
          </p>
          <p className="fw-semibold mb-3">{view.info.inviteeEmail}</p>
          <p className="text-muted small mb-4">
            Be sure to check your spam folder.
          </p>

          {verifyNotice && (
            <div className="alert alert-info" role="status">
              {verifyNotice}
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small text-muted">Verification code</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="form-control"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              autoFocus
              required
            />
            <div className="form-text">
              Enter the 6-digit code from the email to finish accepting your invitation.
            </div>
          </div>

          {submitError && (
            <div className="alert alert-danger" role="alert">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mb-3"
            disabled={verifying || otp.length !== 6}
          >
            {verifying ? 'Verifying…' : 'Verify and accept invitation'}
          </button>

          <div className="d-flex justify-content-between small">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={onResend}
              disabled={resendBusy}
            >
              {resendBusy ? 'Resending…' : 'Resend code'}
            </button>
            <button
              type="button"
              className="btn btn-link p-0 text-muted"
              onClick={onBackFromVerify}
            >
              Back
            </button>
          </div>
        </form>
      )}

      {(view.kind === 'createAccount' || view.kind === 'signIn') && !verifyMode && (
        <form onSubmit={onSubmit} noValidate>
          <h2 className="h4 mb-1">
            {view.kind === 'createAccount' ? 'Welcome to AquaStar' : 'Welcome back'}
          </h2>
          <p className="text-muted mb-4">
            {view.info.inviterDisplayName ? (
              <>
                <strong>{view.info.inviterDisplayName}</strong>{' '}
                {view.kind === 'createAccount'
                  ? 'invited you to AquaStar.'
                  : 'invited you to a pump on your AquaStar account.'}
              </>
            ) : (
              <>You've been invited to AquaStar.</>
            )}
          </p>

          <div className="mb-3">
            <label className="form-label small text-muted">Email</label>
            <input
              type="email"
              className="form-control"
              value={view.info.inviteeEmail}
              readOnly
              aria-readonly="true"
            />
          </div>

          {view.kind === 'createAccount' && (
            <div className="mb-3">
              <label className="form-label small text-muted">
                Your name <span className="text-muted">(optional)</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                autoComplete="name"
                placeholder="e.g. Jordan Reyes"
              />
            </div>
          )}

          <div className="mb-3">
            <label className="form-label small text-muted">
              {view.kind === 'createAccount' ? 'Choose a password' : 'Password'}
            </label>
            <PasswordField
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={
                view.kind === 'createAccount' ? 'new-password' : 'current-password'
              }
              required
              minLength={view.kind === 'createAccount' ? 10 : undefined}
            />
            {view.kind === 'createAccount' && (
              <div className="form-text">At least 10 characters.</div>
            )}
          </div>

          {submitError && (
            <div className="alert alert-danger" role="alert">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100 mb-3"
            disabled={submitting || !password}
          >
            {submitting
              ? view.kind === 'createAccount'
                ? 'Creating your account…'
                : 'Signing you in…'
              : view.kind === 'createAccount'
                ? 'Create account'
                : 'Sign in & accept'}
          </button>

          {/* OAuth alternative — SignIn branch ONLY. The CreateAccount
             branch hides Google deliberately: the invite is bound to a
             specific email (e.g. lisakisses@aol.com), but Google login
             could authenticate the user under a different account
             (lisa@gmail.com). The backend INVITE_EMAIL_MISMATCH check
             catches that, but the UX is confusing — users interpret
             "Continue with Google" as "sign in with any Google account
             I have". For an invite that requires the user to prove
             ownership of the invited email by setting a password,
             surface the password path only. SignIn branch keeps Google
             because by then the user has an existing platform account
             they may have OAuth-linked. (P1-INV17, 2026-05-22.) */}
          {view.kind === 'signIn' && (
            <>
              <div className="d-flex align-items-center my-3" aria-hidden="true">
                <div className="flex-grow-1 border-top" />
                <span className="px-2 small text-muted">or</span>
                <div className="flex-grow-1 border-top" />
              </div>

              <GoogleSignInButton
                label="Sign in with Google"
                // Bring the user BACK to this same invite URL so the
                // OAuth-callback effect can finalize the accept.
                redirectUri={
                  window.location.origin + window.location.pathname
                }
                className="mb-3"
              />
            </>
          )}

          <hr className="my-4" />
          <p className="small text-muted text-center mb-2">
            Or get the AquaStar app:
          </p>
          <StoreBadges />
        </form>
      )}
    </TwoPaneLayout>
  );
}
